import { toRange } from "monaco-languageserver-types";
import type { Diagnostic, Range } from "vscode-languageserver-protocol";

export type ProblemSeverity = "error" | "warning";

export interface ProblemFilter {
	error: boolean;
	warning: boolean;
}

export interface ProblemCounts {
	error: number;
	warning: number;
}

export interface ProblemEntry {
	path: string;
	severity: ProblemSeverity;
	line: number;
	column: number;
	message: string;
	code: string | null;
	range: Range;
}

export interface ProblemFileGroup {
	path: string;
	fileName: string;
	directory: string;
	errorCount: number;
	warningCount: number;
	entries: ProblemEntry[];
}

export interface LimitedProblemGroups {
	groups: ProblemFileGroup[];
	hiddenCount: number;
}

export const MAX_PROBLEM_ROWS = 500;

export const ALL_PROBLEM_FILTER: ProblemFilter = { error: true, warning: true };

/** LSP の診断重要度を Problems パネルで扱う種別に変換する。 */
function problemSeverity(diagnostic: Diagnostic): ProblemSeverity | null {
	if (diagnostic.severity === 2) return "warning";
	if (diagnostic.severity === 3 || diagnostic.severity === 4) return null;
	return "error";
}

/** 1 ファイル分の診断からエラー・警告件数を数える。 */
export function problemCountsForDiagnostics(
	diagnostics: readonly Diagnostic[] | undefined,
): ProblemCounts {
	const counts: ProblemCounts = { error: 0, warning: 0 };
	if (!diagnostics) return counts;
	for (const diagnostic of diagnostics) {
		const severity = problemSeverity(diagnostic);
		if (severity) counts[severity]++;
	}
	return counts;
}

export interface ProblemIndicator {
	/** エラー + 警告の合計件数。 */
	total: number;
	/** エラーがあれば赤、警告のみなら黄、なければ undefined (ファイルツリー・タブの色分け表示に使う)。 */
	color: string | undefined;
}

/** 1 ファイル分の診断から、ファイル名の色分け表示に使う合計件数と色を返す。 */
export function problemIndicatorFor(
	diagnostics: readonly Diagnostic[] | undefined,
): ProblemIndicator {
	const counts = problemCountsForDiagnostics(diagnostics);
	const color =
		counts.error > 0 ? "var(--danger)" : counts.warning > 0 ? "var(--warning)" : undefined;
	return { total: counts.error + counts.warning, color };
}

/**
 * 現在のワークスペースに残るエラー・警告の件数を返す。診断は pyright が同期している
 * ファイルの分だけが残る (削除されたファイルの診断は再同期で消える) ため、ファイル一覧との
 * 突き合わせはしない。
 */
export function countProblems(byPath: Record<string, Diagnostic[]>): ProblemCounts {
	const counts: ProblemCounts = { error: 0, warning: 0 };
	for (const diagnostics of Object.values(byPath)) {
		for (const diagnostic of diagnostics) {
			const severity = problemSeverity(diagnostic);
			if (severity) counts[severity]++;
		}
	}
	return counts;
}

/**
 * 診断をファイル単位で整列して Problems パネル用の表示データへ変換する (フィルタ前の全件)。
 * 重要度フィルタは {@link filterProblemGroups} で適用する — 整列済みの結果を使い回せるようにするため、
 * ここではフィルタを受け取らない。
 */
export function buildProblemGroups(byPath: Record<string, Diagnostic[]>): ProblemFileGroup[] {
	const groups: ProblemFileGroup[] = [];

	for (const [path, diagnostics] of Object.entries(byPath)) {
		const entries = diagnostics.flatMap((diagnostic) => {
			const severity = problemSeverity(diagnostic);
			if (severity === null) return [];
			return [
				{
					path,
					severity,
					line: diagnostic.range.start.line + 1,
					column: diagnostic.range.start.character + 1,
					message: diagnosticMessage(diagnostic),
					code: diagnostic.code == null ? null : String(diagnostic.code),
					range: diagnostic.range,
				},
			];
		});
		if (entries.length === 0) continue;
		entries.sort(compareProblemEntries);
		const slash = path.lastIndexOf("/");
		groups.push({
			path,
			fileName: path.slice(slash + 1),
			directory: slash === -1 ? "" : path.slice(0, slash),
			...countBySeverity(entries),
			entries,
		});
	}

	return groups.sort((a, b) => a.path.localeCompare(b.path));
}

/** 整列済みのグループへ重要度フィルタを適用する。整列済みの順序はそのまま保たれるため再整列しない。 */
export function filterProblemGroups(
	groups: readonly ProblemFileGroup[],
	filter: ProblemFilter,
): ProblemFileGroup[] {
	if (filter.error && filter.warning) return [...groups];
	const filtered: ProblemFileGroup[] = [];
	for (const group of groups) {
		const entries = group.entries.filter((entry) => filter[entry.severity]);
		if (entries.length === 0) continue;
		filtered.push({ ...group, entries, ...countBySeverity(entries) });
	}
	return filtered;
}

/** Problems パネルの行数上限に合わせてグループを途中で切り詰める。 */
export function limitProblemGroups(
	groups: readonly ProblemFileGroup[],
	maxRows = MAX_PROBLEM_ROWS,
): LimitedProblemGroups {
	let remaining = maxRows;
	let hiddenCount = 0;
	const limitedGroups: ProblemFileGroup[] = [];

	for (const group of groups) {
		if (remaining <= 0) {
			hiddenCount += group.entries.length;
			continue;
		}
		if (group.entries.length <= remaining) {
			limitedGroups.push(group);
			remaining -= group.entries.length;
			continue;
		}
		const entries = group.entries.slice(0, remaining);
		limitedGroups.push({ ...group, entries, ...countBySeverity(entries) });
		hiddenCount += group.entries.length - entries.length;
		remaining = 0;
	}

	return { groups: limitedGroups, hiddenCount };
}

/** 0 始まりの LSP 範囲を 1 始まりの Monaco 範囲に変換する。 */
export const lspRangeToMonacoRange = toRange;

function countBySeverity(
	entries: readonly ProblemEntry[],
): Pick<ProblemFileGroup, "errorCount" | "warningCount"> {
	let errorCount = 0;
	let warningCount = 0;
	for (const entry of entries) {
		if (entry.severity === "error") errorCount++;
		else warningCount++;
	}
	return { errorCount, warningCount };
}

function compareProblemEntries(a: ProblemEntry, b: ProblemEntry): number {
	if (a.severity !== b.severity) return a.severity === "error" ? -1 : 1;
	if (a.line !== b.line) return a.line - b.line;
	return a.column - b.column;
}

function diagnosticMessage(diagnostic: Diagnostic): string {
	return typeof diagnostic.message === "string" ? diagnostic.message : diagnostic.message.value;
}
